import { useState } from 'react';

import { ApolloError } from '@apollo/react-hooks';
import { IForgotPasswordOnCompletedResponse, ForgotPasswordForm, useForgotPassword } from '@netfront/gelada-identity-library';
import { Breadcrumbs, IBreadcrumbItem } from '@netfront/ui-library';
import kebabCase from 'lodash.kebabcase';

import { Affiliates } from '../components/Affiliates';
import { BreadcrumbItemHomePageLink, BreadcrumbItem } from '../components/BreadcrumbItems';
import { BUTTON_CLASSES } from '../components/Button';
import { HelpButton } from '../components/HelpButton';
import { Link } from '../components/Link';
import { PageLayout } from '../components/PageLayout';
import { useToast } from '../hooks/useToast';

const PAGE_TITLE = 'Forgot password';

const BREADCRUMB_ITEMS: IBreadcrumbItem[] = [
  {
    content: <BreadcrumbItemHomePageLink />,
    key: 'home',
  },
  {
    content: <BreadcrumbItem text={PAGE_TITLE} />,
    key: kebabCase(PAGE_TITLE),
  },
];

const ForgotPasswordPage = () => {
  const [isForgotPasswordCompleted, setIsForgotPasswordCompleted] = useState<boolean>(false);

  const { handleToastError } = useToast();

  const handleForgotPasswordCompleted = ({ isCompleted }: IForgotPasswordOnCompletedResponse) => {
    setIsForgotPasswordCompleted(isCompleted);
  };

  const handleForgotPasswordError = (error: ApolloError) => {
    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleForgotPassword, isLoading = false } = useForgotPassword({
    onCompleted: handleForgotPasswordCompleted,
    onError: handleForgotPasswordError,
  });

  return (
    <PageLayout isPreloaderVisible={isLoading} title={PAGE_TITLE}>
      <div className="container-md flex justify-end py-8">
        <Breadcrumbs items={BREADCRUMB_ITEMS} />
      </div>

      <section className="container-md pb-8 pt-4">
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 md:mb-0 md:w-4/5 w-full">
            <h1 className="color-accent h4 mb-2 weight-800">{PAGE_TITLE}</h1>
            <h2 className="color-blue-900 h2">Receive a reset link</h2>
            <hr className="bg-grey-100 block border-none h-0-5 ml-0 mt-7 radius-full w-third" role="presentation" />
          </div>

          <div className="align-center flex w-full">
            <div className="bg-grey-100 flex-1 h-0-5 md:flex mx-8 none radius-full" role="presentation" />
            <HelpButton />
          </div>
        </div>
      </section>

      <section className="pb-12 pt-4">
        <div className="container-md flex flex-col md:flex-row">
          <div className="mb-12 md:mb-0 md:mr-24 w-full">
            {!isForgotPasswordCompleted ? (
              <ForgotPasswordForm
                buttonClassName={BUTTON_CLASSES.default}
                buttonText="Submit"
                isSubmitting={isLoading}
                onForgotPassword={(email) =>
                  handleForgotPassword({
                    email,
                  })
                }
              />
            ) : (
              <>
                <div className="mb-8 md:mb-8">An email has been sent with a reset password link to your nominated email.</div>

                <div className="mb-8 md:mb-8">
                  To finalise your password reset, click the link within the email and follow the prompts to complete the reset password
                  process.
                </div>

                <Link href="/login" theme="default">
                  Return to login
                </Link>
              </>
            )}
          </div>
          <div className="flex justify-center md:w-3/4 w-full">
            <img alt="Cartoon of user engaging with the CarerWell program" loading="lazy" src="/images/pre-chapter-illustration.svg" />
          </div>
        </div>
      </section>

      <Affiliates />
    </PageLayout>
  );
};

// eslint-disable-next-line import/no-default-export
export default ForgotPasswordPage;
